export const serviceCategories = [
  { id: 'all', label: 'All Services' },
  { id: 'strategy-design', label: 'Strategy & Design' },
  { id: 'development', label: 'Development' },
  { id: 'cloud-ai', label: 'Cloud & AI' },
  { id: 'quality-support', label: 'Quality & Support' },
  { id: 'talent', label: 'Talent Solutions' },
];

export const services = [
  {
    id: 'product-discovery-strategy',
    title: 'Product Discovery & Strategy',
    icon: 'ri-compass-discover-line',
    shortDescription:
      'Turn your vision into a clear product roadmap with market validation, feature planning, architecture consulting, and technology recommendations.',
    fullDescription:
      'We help you validate ideas, define MVPs, and build a strategic roadmap that aligns product development with business outcomes. From competitive analysis to architecture consulting, our discovery process reduces risk and accelerates time-to-market.',
    category: 'strategy-design',
    features: [
      'Market & Competitor Research',
      'MVP Definition & Prioritization',
      'Technical Architecture Consulting',
      'Technology Stack Recommendations',
      'Product Roadmap Planning',
      'Feasibility Analysis',
    ],
    stat: '40%',
    statLabel: 'faster time-to-market',
  },
  {
    id: 'ui-ux-design',
    title: 'UI/UX Design Services',
    icon: 'ri-palette-line',
    shortDescription:
      'Deliver intuitive and visually engaging digital experiences with user-centric design systems and interactive interfaces.',
    fullDescription:
      'Our design team creates user-centered interfaces that drive engagement and conversion. From wireframes to high-fidelity prototypes, we build design systems that scale across platforms while maintaining brand consistency.',
    category: 'strategy-design',
    features: [
      'User Research & Personas',
      'Wireframing & Prototyping',
      'Design Systems',
      'Usability Testing',
      'Interaction Design',
      'Visual Identity Integration',
    ],
    stat: '3x',
    statLabel: 'higher user engagement',
  },
  {
    id: 'custom-software-development',
    title: 'Custom Software Development',
    icon: 'ri-code-box-line',
    shortDescription:
      'Develop tailor-made web, mobile, and cloud applications aligned with your business workflows and customer needs.',
    fullDescription:
      'We build bespoke software solutions that solve your unique business challenges. Our full-stack teams work across modern frameworks to deliver secure, scalable applications that integrate seamlessly with your existing infrastructure.',
    category: 'development',
    features: [
      'Full-Stack Web Development',
      'Enterprise Applications',
      'Legacy System Modernization',
      'Microservices Architecture',
      'Third-Party Integrations',
      'Scalable Backend Systems',
    ],
    stat: '150+',
    statLabel: 'custom apps delivered',
  },
  {
    id: 'saas-product-development',
    title: 'SaaS Product Development',
    icon: 'ri-cloud-line',
    shortDescription:
      'Launch scalable multi-tenant SaaS platforms with subscription models, secure infrastructure, analytics, and seamless user experiences.',
    fullDescription:
      'We design and build cloud-native SaaS platforms from the ground up. From multi-tenant architecture to billing integrations and admin dashboards, we handle the full product lifecycle so you can focus on growth.',
    category: 'development',
    features: [
      'Multi-Tenant Architecture',
      'Subscription & Billing Systems',
      'Tenant Isolation & Security',
      'Admin & Analytics Dashboards',
      'API-First Design',
      'CI/CD for SaaS Delivery',
    ],
    stat: '35+',
    statLabel: 'SaaS products launched',
  },
  {
    id: 'web-application-development',
    title: 'Web Application Development',
    icon: 'ri-global-line',
    shortDescription:
      'Build responsive, high-performance web applications using modern frameworks and scalable backend technologies.',
    fullDescription:
      'Our web development teams build fast, responsive, and SEO-friendly applications using React, Next.js, Vue, Node.js, and Python. We focus on performance, accessibility, and conversion-optimized user experiences.',
    category: 'development',
    features: [
      'React, Next.js & Vue Frontends',
      'Node.js & Python Backends',
      'Progressive Web Apps',
      'Server-Side Rendering',
      'Performance Optimization',
      'SEO-First Architecture',
    ],
    stat: '200+',
    statLabel: 'web apps shipped',
  },
  {
    id: 'mobile-app-development',
    title: 'Mobile App Development',
    icon: 'ri-smartphone-line',
    shortDescription:
      'Create intuitive Android and iOS applications that deliver engaging user experiences and business efficiency.',
    fullDescription:
      'We build native and cross-platform mobile applications that users love. From consumer apps to enterprise mobility solutions, our teams handle everything from UI/UX to app store deployment and ongoing maintenance.',
    category: 'development',
    features: [
      'iOS & Android Native Apps',
      'React Native & Flutter',
      'App Store Deployment',
      'Push Notifications & Analytics',
      'Offline-First Architecture',
      'Mobile Security & Compliance',
    ],
    stat: '80+',
    statLabel: 'mobile apps launched',
  },
  {
    id: 'api-system-integrations',
    title: 'API & System Integrations',
    icon: 'ri-plug-line',
    shortDescription:
      'Enable smooth communication between platforms, CRMs, ERPs, payment gateways, third-party services, and enterprise systems.',
    fullDescription:
      'We build robust APIs and integration layers that connect your systems and unlock data flow across your organization. From REST and GraphQL APIs to enterprise middleware, we ensure reliable, secure, and scalable integrations.',
    category: 'development',
    features: [
      'REST & GraphQL API Development',
      'CRM & ERP Integrations',
      'Payment Gateway Integration',
      'Enterprise Middleware',
      'EDI & B2B Integrations',
      'API Gateway & Management',
    ],
    stat: '300+',
    statLabel: 'APIs built & integrated',
  },
  {
    id: 'cloud-devops-engineering',
    title: 'Cloud & DevOps Engineering',
    icon: 'ri-server-line',
    shortDescription:
      'Accelerate deployments and improve infrastructure scalability with CI/CD pipelines, cloud-native architecture, automation, and monitoring.',
    fullDescription:
      'Our DevOps engineers architect cloud-native infrastructure on AWS, Azure, and GCP. We implement CI/CD pipelines, Infrastructure as Code, container orchestration, and monitoring to ensure your systems are reliable, scalable, and cost-efficient.',
    category: 'cloud-ai',
    features: [
      'AWS, Azure & GCP Architecture',
      'CI/CD Pipeline Implementation',
      'Kubernetes & Container Orchestration',
      'Infrastructure as Code',
      'Monitoring & Observability',
      'Cost Optimization & Security',
    ],
    stat: '60%',
    statLabel: 'faster deployment cycles',
  },
  {
    id: 'ai-automation-solutions',
    title: 'AI & Automation Solutions',
    icon: 'ri-brain-line',
    shortDescription:
      'Leverage AI-powered capabilities including chatbots, workflow automation, predictive analytics, recommendation systems, and intelligent business tools.',
    fullDescription:
      'We help you unlock the power of AI with practical, production-ready solutions. From intelligent chatbots and workflow automation to predictive analytics and recommendation engines, we build AI systems that deliver measurable ROI.',
    category: 'cloud-ai',
    features: [
      'Conversational AI & Chatbots',
      'Predictive Analytics',
      'Recommendation Engines',
      'Intelligent Workflow Automation',
      'NLP & Document Processing',
      'MLOps & Model Management',
    ],
    stat: '25+',
    statLabel: 'AI solutions deployed',
  },
  {
    id: 'qa-testing-services',
    title: 'QA & Testing Services',
    icon: 'ri-bug-line',
    shortDescription:
      'Ensure software reliability with manual testing, automation testing, performance testing, security testing, and continuous quality assurance.',
    fullDescription:
      'Our QA teams ensure your software meets the highest standards. We implement comprehensive testing strategies including automated test suites, performance benchmarking, security audits, and continuous quality gates in your delivery pipeline.',
    category: 'quality-support',
    features: [
      'Manual & Automated Testing',
      'Performance & Load Testing',
      'Security & Penetration Testing',
      'API & Integration Testing',
      'Test Automation Frameworks',
      'CI/CD Quality Gates',
    ],
    stat: '99.2%',
    statLabel: 'defect detection rate',
  },
  {
    id: 'product-maintenance-support',
    title: 'Product Maintenance & Support',
    icon: 'ri-tools-line',
    shortDescription:
      'Keep your applications secure, optimized, and up-to-date with ongoing support, monitoring, upgrades, and performance enhancements.',
    fullDescription:
      'We provide proactive maintenance and 24/7 support to keep your applications running smoothly. From security patches and performance tuning to feature enhancements and legacy modernization, we ensure your software stays competitive.',
    category: 'quality-support',
    features: [
      '24/7 Application Monitoring',
      'Security Patches & Updates',
      'Performance Tuning',
      'Bug Fixes & Hotfixes',
      'Feature Enhancements',
      'Legacy System Modernization',
    ],
    stat: '99.9%',
    statLabel: 'uptime SLA',
  },
  {
    id: 'dedicated-developers',
    title: 'Dedicated Developers',
    icon: 'ri-user-star-line',
    shortDescription:
      'Hire full-time remote developers exclusively aligned with your project goals and delivery timelines.',
    fullDescription:
      'Build your extended engineering team with dedicated developers who work exclusively on your projects. Our rigorous vetting process ensures you get senior talent that integrates seamlessly with your workflows and culture.',
    category: 'talent',
    features: [
      'Full-Time Dedicated Engineers',
      'Direct Communication & Management',
      'Agile & Scrum Integration',
      'Time Zone Alignment',
      'Code Quality Standards',
      'Long-Term Retention Focus',
    ],
    stat: '200+',
    statLabel: 'engineers in network',
  },
  {
    id: 'contract-staffing',
    title: 'Contract Staffing',
    icon: 'ri-file-list-3-line',
    shortDescription:
      'Quickly onboard experienced professionals for short-term, long-term, or project-based requirements.',
    fullDescription:
      'Scale your team up or down with flexible contract staffing. Whether you need a specialist for a 3-month sprint or a long-term contractor, we match you with pre-vetted professionals who can start contributing immediately.',
    category: 'talent',
    features: [
      'Short & Long-Term Contracts',
      'Quick Onboarding (48h)',
      'Flexible Renewals',
      'Performance Tracking',
      'Specialist Skill Matching',
      'Compliance & Payroll Handling',
    ],
    stat: '48h',
    statLabel: 'avg. time to match',
  },
  {
    id: 'offshore-development-teams',
    title: 'Offshore Development Teams',
    icon: 'ri-earth-line',
    shortDescription:
      'Reduce operational costs while maintaining high-quality engineering delivery with offshore technology teams.',
    fullDescription:
      'Establish a high-performing offshore development center with our end-to-end team setup. We handle recruitment, infrastructure, compliance, and management so you can focus on product outcomes while reducing costs by up to 40%.',
    category: 'talent',
    features: [
      'End-to-End Team Setup',
      'Cost-Effective Delivery',
      'Time Zone Coverage',
      'Cultural Compatibility Training',
      'Secure Infrastructure',
      'Transparent Reporting',
    ],
    stat: '32%',
    statLabel: 'avg. cost reduction',
  },
  {
    id: 'project-based-engagement',
    title: 'Project-Based Engagement',
    icon: 'ri-folder-chart-line',
    shortDescription:
      'Build specialized teams for product launches, migrations, digital transformation initiatives, or enterprise modernization projects.',
    fullDescription:
      'For fixed-scope projects with clear deliverables, our project-based engagement model gives you a complete team with defined timelines and budgets. We manage the entire delivery lifecycle from planning to deployment and handover.',
    category: 'talent',
    features: [
      'Fixed Scope & Budget',
      'Complete Project Team',
      'Defined Milestones & Deliverables',
      'Agile Project Management',
      'Risk Mitigation',
      'Knowledge Transfer & Documentation',
    ],
    stat: '120+',
    statLabel: 'projects delivered',
  },
];